import type { ChainModifiers, Entry, EntryFieldTypes, EntrySkeletonType, LocaleCode } from "contentful";
import type { TypeTileSkeleton } from "./TypeTile";

/**
 * Fields type definition for content type 'TypeAutomated_content_tile'
 * @name TypeAutomated_content_tileFields
 * @type {TypeAutomated_content_tileFields}
 * @memberof TypeAutomated_content_tile
 */
export interface TypeAutomated_content_tileFields {
    /**
     * Field type definition for field 'name' (Name)
     * @name Name
     * @localized false
     */
    name: EntryFieldTypes.Symbol;
    /**
     * Field type definition for field 'section_id' (Section ID)
     * @name Section ID
     * @localized false
     * @summary Section ID used for navigation. Note: Only applicable when this component is being used as a section.
     */
    section_id?: EntryFieldTypes.Symbol;
    /**
     * Field type definition for field 'start_date_source' (Start Date Source)
     * @name Start Date Source
     * @localized false
     * @summary If no selection is made, the custom start date from the first content will be used instead.
     */
    start_date_source?: EntryFieldTypes.Symbol<"Pricing API" | "Program Meta (Product API)">;
    /**
     * Field type definition for field 'start_date_preview' (Start Date Preview)
     * @name Start Date Preview
     * @localized false
     * @summary Preview Start Date from API.
     */
    start_date_preview?: EntryFieldTypes.Symbol<"False" | "True">;
    /**
     * Field type definition for field 'days_before_start_date' (Show Content XX Days Before Start Date (API Only))
     * @name Show Content XX Days Before Start Date (API Only)
     * @localized false
     * @summary This is the day the automation starts and first content will show. ex: Program start date: 9/20/25, if set to 10, first content will show on 9/10/25.
     */
    days_before_start_date?: EntryFieldTypes.Integer;
    /**
     * Field type definition for field 'content_custom_end_date' (Content Custom End Date)
     * @name Content Custom End Date
     * @localized false
     * @summary Doesn't work with Start Date from APIs
     */
    content_custom_end_date?: EntryFieldTypes.Date;
    /**
     * Field type definition for field 'first_content_custom_start_date' (First Content Custom Start Date)
     * @name First Content Custom Start Date
     * @localized false
     * @summary Doesn't work with Start Date from APIs
     */
    first_content_custom_start_date?: EntryFieldTypes.Date;
    /**
     * Field type definition for field 'first_content' (First Content)
     * @name First Content
     * @localized false
     */
    first_content?: EntryFieldTypes.EntryLink<TypeTileSkeleton>;
    /**
     * Field type definition for field 'first_content_duration' (First Content Duration (Days))
     * @name First Content Duration (Days)
     * @localized false
     * @summary How long the first content will show, before changing to the second content. If no content is next, content will be removed.
     */
    first_content_duration?: EntryFieldTypes.Integer;
    /**
     * Field type definition for field 'first_content_preview' (Preview First Content)
     * @name Preview First Content
     * @localized false
     * @summary Enable preview for the first content.
     */
    first_content_preview?: EntryFieldTypes.Symbol<"False" | "True">;
    /**
     * Field type definition for field 'second_content_custom_start_date' (Second Content Custom Start Date)
     * @name Second Content Custom Start Date
     * @localized false
     * @summary Doesn't work with Start Date from APIs
     */
    second_content_custom_start_date?: EntryFieldTypes.Date;
    /**
     * Field type definition for field 'second_content' (Second Content)
     * @name Second Content
     * @localized false
     */
    second_content?: EntryFieldTypes.EntryLink<TypeTileSkeleton>;
    /**
     * Field type definition for field 'second_content_duration' (Second Content Duration (Days))
     * @name Second Content Duration (Days)
     * @localized false
     * @summary How long the second content will show, before changing to the third content. If no content is next, content will be removed.
     */
    second_content_duration?: EntryFieldTypes.Integer;
    /**
     * Field type definition for field 'second_content_preview' (Preview Second Content)
     * @name Preview Second Content
     * @localized false
     * @summary Enable preview for the second content.
     */
    second_content_preview?: EntryFieldTypes.Symbol<"False" | "True">;
    /**
     * Field type definition for field 'third_content_custom_start_date' (Third Content Custom Start Date)
     * @name Third Content Custom Start Date
     * @localized false
     * @summary Doesn't work with Start Date from APIs
     */
    third_content_custom_start_date?: EntryFieldTypes.Date;
    /**
     * Field type definition for field 'third_content' (Third Content)
     * @name Third Content
     * @localized false
     */
    third_content?: EntryFieldTypes.EntryLink<TypeTileSkeleton>;
    /**
     * Field type definition for field 'third_content_duration' (Third Content Duration (Days))
     * @name Third Content Duration (Days)
     * @localized false
     * @summary How long the third content will show, before being removed.
     */
    third_content_duration?: EntryFieldTypes.Integer;
    /**
     * Field type definition for field 'third_content_preview' (Preview Third Content)
     * @name Preview Third Content
     * @localized false
     * @summary Enable preview for the third content.
     */
    third_content_preview?: EntryFieldTypes.Symbol<"False" | "True">;
}

/**
 * Entry skeleton type definition for content type 'automated_content_tile' (Automated Content - Tile)
 * @name TypeAutomated_content_tileSkeleton
 * @type {TypeAutomated_content_tileSkeleton}
 * @author 5PB52A0oK19dIz30b5sbTm
 * @since 2026-04-29T04:13:37.816Z
 * @version 3
 */
export type TypeAutomated_content_tileSkeleton = EntrySkeletonType<TypeAutomated_content_tileFields, "automated_content_tile">;
/**
 * Entry type definition for content type 'automated_content_tile' (Automated Content - Tile)
 * @name TypeAutomated_content_tile
 * @type {TypeAutomated_content_tile}
 * @author 5PB52A0oK19dIz30b5sbTm
 * @since 2026-04-29T04:13:37.816Z
 * @version 3
 */
export type TypeAutomated_content_tile<Modifiers extends ChainModifiers, Locales extends LocaleCode = LocaleCode> = Entry<TypeAutomated_content_tileSkeleton, Modifiers, Locales>;

export function isTypeAutomated_content_tile<Modifiers extends ChainModifiers, Locales extends LocaleCode>(entry: Entry<EntrySkeletonType, Modifiers, Locales>): entry is TypeAutomated_content_tile<Modifiers, Locales> {
    return entry.sys.contentType.sys.id === 'automated_content_tile'
}

export type TypeAutomated_content_tileWithoutLinkResolutionResponse = TypeAutomated_content_tile<"WITHOUT_LINK_RESOLUTION">;
export type TypeAutomated_content_tileWithoutUnresolvableLinksResponse = TypeAutomated_content_tile<"WITHOUT_UNRESOLVABLE_LINKS">;
export type TypeAutomated_content_tileWithAllLocalesResponse<Locales extends LocaleCode = LocaleCode> = TypeAutomated_content_tile<"WITH_ALL_LOCALES", Locales>;
export type TypeAutomated_content_tileWithAllLocalesAndWithoutLinkResolutionResponse<Locales extends LocaleCode = LocaleCode> = TypeAutomated_content_tile<"WITHOUT_LINK_RESOLUTION" | "WITH_ALL_LOCALES", Locales>;
export type TypeAutomated_content_tileWithAllLocalesAndWithoutUnresolvableLinksResponse<Locales extends LocaleCode = LocaleCode> = TypeAutomated_content_tile<"WITHOUT_UNRESOLVABLE_LINKS" | "WITH_ALL_LOCALES", Locales>;
